import { RefObject, useEffect, useRef } from 'react'

export const GROUP_WORKOUT_COUNTDOWN_SECONDS = 3

export const getGroupWorkoutCountdownOffset = (timeLeft: number): number | null => {
  if (!Number.isFinite(timeLeft) || timeLeft <= 0 || timeLeft > GROUP_WORKOUT_COUNTDOWN_SECONDS) {
    return null
  }

  return Math.min(
    GROUP_WORKOUT_COUNTDOWN_SECONDS,
    Math.max(0, GROUP_WORKOUT_COUNTDOWN_SECONDS - timeLeft),
  )
}

interface GroupWorkoutCountdownCueOptions {
  audioRef: RefObject<HTMLAudioElement>
  countdownKey: string | null
  isPaused: boolean
  playMedia: (media: HTMLMediaElement | null) => void
  timeLeft: number
  volume: number
}

const useGroupWorkoutCountdownCue = ({
  audioRef,
  countdownKey,
  isPaused,
  playMedia,
  timeLeft,
  volume,
}: GroupWorkoutCountdownCueOptions) => {
  const activeCountdownKeyRef = useRef<string | null>(null)
  const hasPlayedRef = useRef(false)
  const shouldResumeRef = useRef(false)
  // The first timeLeft seen for a new key is still the previous segment's final
  // value (just above zero), which would replay the cue's last beep.
  const staleTimeLeftRef = useRef<number | null>(null)

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    audio.volume = volume

    if (activeCountdownKeyRef.current !== countdownKey) {
      activeCountdownKeyRef.current = countdownKey
      hasPlayedRef.current = false
      shouldResumeRef.current = false
      staleTimeLeftRef.current = timeLeft
    }

    if (!countdownKey) return

    if (staleTimeLeftRef.current !== null) {
      if (timeLeft === staleTimeLeftRef.current) return
      staleTimeLeftRef.current = null
    }

    if (isPaused) {
      audio.pause()
      shouldResumeRef.current = hasPlayedRef.current
      return
    }

    const playbackOffset = getGroupWorkoutCountdownOffset(timeLeft)
    if (playbackOffset === null || (hasPlayedRef.current && !shouldResumeRef.current)) return

    audio.currentTime = playbackOffset
    hasPlayedRef.current = true
    shouldResumeRef.current = false
    playMedia(audio)
  }, [audioRef, countdownKey, isPaused, playMedia, timeLeft, volume])
}

export default useGroupWorkoutCountdownCue
